import React, { memo, useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { NOTICIA_CONTENIDO_PURIFY, sanitizeArticleHtml } from '../../lib/sanitizeArticleHtml.js';
import { DCard, BtnGold, BtnGhost } from '../components/MarketplaceAtoms.js';
import {
  PARTNER_NOTICIAS_IMAGENES_BUCKET,
  fetchPartnerNewsDrafts,
  insertPartnerNewsDraft,
  partnerNewsImageObjectPath,
  updatePartnerNewsDraftImageUrl,
} from '../../lib/partnerNewsService.js';

const CAT_OPTIONS = ['Vinilo', 'Mercado', 'Eventos', 'Subastas', 'Comunidad'];

const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
};

function formatFecha(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ts));
  } catch {
    return '—';
  }
}

const SectionNoticiasPartner = memo(function SectionNoticiasPartner({ partner, onError, onNotifySuccess }) {
  const [drafts, setDrafts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [extracto, setExtracto] = useState('');
  const [categoria, setCategoria] = useState('Vinilo');
  const [tiempoLectura, setTiempoLectura] = useState('');
  const [imagenUrl, setImagenUrl] = useState('');
  const [contenidoHtml, setContenidoHtml] = useState('');
  const [archivoImagen, setArchivoImagen] = useState(null);

  const loadDrafts = useCallback(async () => {
    if (!partner?.id) return;
    setLoadingList(true);
    onError('');
    const { data, error } = await fetchPartnerNewsDrafts(partner.id);
    if (error) {
      onError(error);
      setDrafts([]);
    } else {
      setDrafts(data ?? []);
    }
    setLoadingList(false);
  }, [partner?.id, onError]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const resetForm = () => {
    setTitulo('');
    setExtracto('');
    setCategoria('Vinilo');
    setTiempoLectura('');
    setImagenUrl('');
    setContenidoHtml('');
    setArchivoImagen(null);
  };

  const submit = async () => {
    if (!partner?.id) return;
    const t = titulo.trim();
    if (t.length < 3) {
      onError('El título debe tener al menos 3 caracteres.');
      return;
    }
    const urlTrim = imagenUrl.trim();
    if (urlTrim && !/^https:\/\//i.test(urlTrim)) {
      onError('Si indicas imagen por URL, debe empezar por https://');
      return;
    }
    const bodyHtml = sanitizeArticleHtml(contenidoHtml);
    if (!bodyHtml || bodyHtml.replace(/<[^>]+>/g, '').trim().length < 20) {
      onError('El cuerpo (HTML) es demasiado corto o vacío tras la revisión de seguridad.');
      return;
    }

    let ext = null;
    if (archivoImagen) {
      const name = String(archivoImagen.name || '');
      const extByMime = archivoImagen.type ? MIME_TO_EXT[archivoImagen.type] : null;
      const extByName = /\.jpe?g$/i.test(name) ? 'jpg' : /\.png$/i.test(name) ? 'png' : /\.gif$/i.test(name) ? 'gif' : '';
      ext = extByMime || extByName;
      if (!ext) {
        onError('Imagen: usa JPG, PNG o GIF.');
        return;
      }
    }

    setSaving(true);
    onError('');
    try {
      const { row, error: insErr } = await insertPartnerNewsDraft({
        partnerId: partner.id,
        titulo: t,
        extracto: extracto.trim() || null,
        categoria,
        contenido_html: bodyHtml,
        tiempo_lectura: tiempoLectura.trim() || null,
        imagen_url: !archivoImagen && urlTrim ? urlTrim : null,
      });
      if (insErr || !row?.id) {
        onError(insErr || 'No se pudo crear el borrador.');
        return;
      }
      const noticiaId = row.id;

      if (archivoImagen && ext) {
        const objectPath = partnerNewsImageObjectPath(partner.id, noticiaId, ext);
        if (!objectPath) {
          onError('No se pudo generar la ruta de imagen.');
          return;
        }
        const contentType =
          archivoImagen.type ||
          (ext === 'jpg' ? 'image/jpeg' : ext === 'png' ? 'image/png' : 'image/gif');
        const { error: upErr } = await supabase.storage.from(PARTNER_NOTICIAS_IMAGENES_BUCKET).upload(objectPath, archivoImagen, {
          contentType,
          upsert: true,
        });
        if (upErr) {
          onError(
            `Borrador creado (${noticiaId.slice(0, 8)}…), pero la subida de imagen falló: ${upErr.message}. Puedes indicar una URL o contactar soporte.`
          );
          resetForm();
          await loadDrafts();
          return;
        }
        const { data: pub } = supabase.storage.from(PARTNER_NOTICIAS_IMAGENES_BUCKET).getPublicUrl(objectPath);
        const pubUrl = pub?.publicUrl ?? null;
        if (pubUrl) {
          const { error: uImg } = await updatePartnerNewsDraftImageUrl(noticiaId, pubUrl);
          if (uImg) {
            onError(`Borrador creado; no se pudo guardar la URL de imagen: ${uImg}`);
            resetForm();
            await loadDrafts();
            return;
          }
        }
      }

      onNotifySuccess?.(
        'Noticia enviada como borrador. El equipo DreamVinyl la revisará antes de publicarla.'
      );
      resetForm();
      await loadDrafts();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <DCard style={{ padding: 18, marginBottom: 16, background: 'rgba(200,168,75,0.06)', borderColor: 'rgba(200,168,75,0.22)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16, color: C.gold }}>Proponer noticia</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
          Redacta una noticia para la sección Noticias del sitio. Se guardará como <strong style={{ color: '#fff' }}>borrador</strong> (
          <code style={{ color: 'rgba(255,255,255,0.65)' }}>publicado = false</code>) hasta que un administrador la apruebe desde el panel de curación.
        </p>
      </DCard>

      <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 14 }}>Nuevo envío</div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>Título</div>
          <input
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            placeholder="Título de la noticia"
            maxLength={200}
            style={{
              width: '100%',
              maxWidth: 560,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>Extracto (opcional)</div>
          <textarea
            value={extracto}
            onChange={e => setExtracto(e.target.value)}
            placeholder="Resumen breve para listados"
            rows={2}
            maxLength={500}
            style={{
              width: '100%',
              maxWidth: 560,
              padding: 10,
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>Categoría</div>
          <select
            value={categoria}
            onChange={e => setCategoria(e.target.value)}
            style={{
              maxWidth: 280,
              width: '100%',
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
            }}
          >
            {CAT_OPTIONS.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>Tiempo de lectura (opcional)</div>
          <input
            value={tiempoLectura}
            onChange={e => setTiempoLectura(e.target.value)}
            placeholder="Ej: 3 min"
            maxLength={40}
            style={{
              width: '100%',
              maxWidth: 200,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>Imagen — URL https (opcional)</div>
          <input
            value={imagenUrl}
            onChange={e => setImagenUrl(e.target.value)}
            placeholder="https://…"
            disabled={!!archivoImagen}
            style={{
              width: '100%',
              maxWidth: 560,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: archivoImagen ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>
            Imagen — archivo JPG/PNG/GIF (opcional; si eliges archivo, se ignorará la URL)
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={e => {
              const f = e.target.files?.[0] ?? null;
              setArchivoImagen(f);
              if (f) setImagenUrl('');
            }}
            style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 5 }}>
            Cuerpo (HTML permitido: párrafos, títulos h2–h4, enlaces, listas, énfasis)
          </div>
          <textarea
            value={contenidoHtml}
            onChange={e => setContenidoHtml(e.target.value)}
            placeholder={'<p>Escribe el artículo en HTML simple.</p>'}
            rows={12}
            style={{
              width: '100%',
              maxWidth: 720,
              padding: 10,
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 12,
              fontFamily: 'ui-monospace, monospace',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
            Vista previa saneada:{' '}
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(contenidoHtml || '', NOTICIA_CONTENIDO_PURIFY),
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <BtnGold type="button" style={{ fontSize: 12 }} disabled={saving} onClick={() => void submit()}>
            {saving ? 'Enviando…' : 'Enviar a revisión'}
          </BtnGold>
          <BtnGhost type="button" style={{ fontSize: 12 }} disabled={saving} onClick={resetForm}>
            Limpiar formulario
          </BtnGhost>
        </div>
      </DCard>

      <DCard style={{ padding: 18, borderColor: 'rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Tus borradores enviados</div>
          <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => loadDrafts()} disabled={loadingList}>
            {loadingList ? 'Cargando…' : 'Actualizar'}
          </BtnGhost>
        </div>
        {loadingList ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
        ) : drafts.length === 0 ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Aún no has enviado noticias desde este panel.</div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {drafts.map(d => (
              <li
                key={d.id}
                style={{
                  padding: '12px 0',
                  borderTop: '0.5px solid rgba(255,255,255,0.08)',
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.82)',
                }}
              >
                <div style={{ fontWeight: 700, color: '#fff' }}>{d.titulo}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  {d.categoria} · {formatFecha(d.created_at)} · En revisión
                </div>
              </li>
            ))}
          </ul>
        )}
      </DCard>
    </div>
  );
});

export default SectionNoticiasPartner;
